import {
  ChangeEvent,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { yupResolver } from '@hookform/resolvers/yup'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { updateTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/api-actions'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { tasks } from 'src/Constants/tikTokTasks'
import { VideoExamplesModal } from 'src/Components/VideoExamplesModal'
import { videoExamplesByTaskType } from 'src/Constants/tiktokVideoExamples'
import { PlayButton } from 'src/Components/Buttons/PlayButton'

import { TaskTypeRadio } from './TaskTypeRadio'
import { CreatorTaskModalButtons } from './CreatorTaskModalButtons'
import { LyricsLinkField } from './LyricsLinkField'
import {
  CommentFormValues,
  LIPSYNC_TASK_TYPE,
  commentSchema,
  lyricsLinkServerErrors,
} from './commentSchema'

const Container = styled.div`
  display: flex;
  min-height: 844px;
  padding: 64px 0 80px 0;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin: 0 auto;
  width: 600px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
    padding: 24px 16px 0;
    height: 100%;
  }
`

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: start;
  width: 100%;
  font-family: 'Segoe UI', sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 80%;
    align-self: start;
  }
`

const Label = styled.span`
  font-family: 'Segoe UI', sans-serif;
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const Description = styled.div`
  font-family: 'Segoe UI', sans-serif;
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

const StyledTypeTaskRadio = styled(TaskTypeRadio)`
  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    margin-bottom: 75px;
  }
`

export const TiktokScheduleCommentModal: FC = () => {
  const dispatch = useAppDispatch()
  const scheduledCampaign = useAppSelector(getScheduledTikTokCampaign)

  const { setSearch } = useCustomSearchParams()
  const handleClose = (): void => {
    setSearch({ modal: null })
  }

  const [examplesTask, setExamplesTask] = useState<{
    title: string
    value: string
  } | null>(null)

  const handleOpenExamples = useCallback(
    (e: MouseEvent, task: { title: string; value: string }) => {
      e.preventDefault()
      e.stopPropagation()
      setExamplesTask(task)
    },
    [],
  )

  const handleCloseExamples = useCallback(() => {
    setExamplesTask(null)
  }, [])

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
    setValue,
  } = useForm<CommentFormValues>({
    mode: 'onChange',
    resolver: yupResolver(commentSchema),
  })

  useEffect(() => {
    if (scheduledCampaign?.commentType) {
      setValue('taskType', scheduledCampaign?.commentType || '')
    }
  }, [scheduledCampaign?.commentType, setValue])

  useEffect(() => {
    if (scheduledCampaign?.lyricsLink) {
      setValue('lyricsLink', scheduledCampaign.lyricsLink)
    }
  }, [scheduledCampaign?.lyricsLink, setValue])

  const onSubmit = ({ taskType, lyricsLink }: CommentFormValues): void => {
    const selectedTask = tasks.find((type) => type.value === taskType)
    const isLipsync = taskType === LIPSYNC_TASK_TYPE

    void dispatch(
      updateTikTokCampaign({
        id: Number(scheduledCampaign.id),
        ...(scheduledCampaign.startDate && {
          startDate: dayjs(scheduledCampaign.startDate).toISOString(),
        }),
        commentType: taskType,
        comment: selectedTask?.artistDescription || '',
        ...(isLipsync && { lyricsLink: lyricsLink?.trim() ?? '' }),
      }),
    )
      .unwrap()
      .then((res) => {
        handleClose()
        return res
      })
      .catch((error: { message?: string }) => {
        const lyricsLinkError =
          error?.message && lyricsLinkServerErrors[error.message]
        if (lyricsLinkError) {
          setError('lyricsLink', { message: lyricsLinkError })
        }
        return false
      })
  }

  return (
    <FullScreenModal
      showCloseButton
      open
      onCancel={handleClose}
      prefixCls='comment'
    >
      <Container>
        <Title>How should creators use your track?</Title>
        <Controller
          name='taskType'
          control={control}
          rules={{ deps: 'lyricsLink' }}
          render={({ field }) => (
            <RadioContainer>
              {tasks.slice(1).map((radio) => (
                <TaskTypeRadio
                  key={radio.value}
                  label={<Label>{radio.title}</Label>}
                  checked={field.value === radio.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      field.onChange(radio.value)
                    }
                  }}
                  id={radio.value}
                  name='taskType'
                  extra={
                    <PlayButton
                      text='Examples'
                      onClick={(e) => handleOpenExamples(e, radio)}
                    />
                  }
                  footer={
                    radio.value === LIPSYNC_TASK_TYPE &&
                    field.value === LIPSYNC_TASK_TYPE && (
                      <Controller
                        name='lyricsLink'
                        control={control}
                        render={({
                          field: lyricsLinkField,
                          fieldState: { error },
                        }) => (
                          <LyricsLinkField
                            value={lyricsLinkField.value}
                            error={error?.message}
                            onChange={lyricsLinkField.onChange}
                            onBlur={lyricsLinkField.onBlur}
                          />
                        )}
                      />
                    )
                  }
                >
                  <Description>{radio.artistDescription}</Description>
                </TaskTypeRadio>
              ))}
              <StyledTypeTaskRadio
                key={tasks[0].value}
                label={<Label>{tasks[0].title}</Label>}
                checked={field.value === tasks[0].value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    field.onChange(tasks[0].value)
                  }
                }}
                id={tasks[0].value}
                name='taskType'
              >
                <Description>{tasks[0].artistDescription}</Description>
              </StyledTypeTaskRadio>
            </RadioContainer>
          )}
        />
        <CreatorTaskModalButtons
          onClickGoBack={handleClose}
          onClickAddTask={handleSubmit(onSubmit)}
          disabledAdd={!isValid}
        />
      </Container>
      {examplesTask && (
        <VideoExamplesModal
          isOpen={Boolean(examplesTask)}
          onClose={handleCloseExamples}
          title={examplesTask.title}
          videoUrls={videoExamplesByTaskType[examplesTask.value] || []}
        />
      )}
    </FullScreenModal>
  )
}
